import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, X, Loader } from 'lucide-react';
import { searchLocations, zipToLocation } from '../services/geocodingService';

export default function LocationPicker({ value, onChange, placeholder = "Enter ZIP or location name" }) {
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeout = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    // Set input value from prop
    if (value?.displayName) {
      setInputValue(value.shortName || value.displayName);
    }
  }, [value]);

  useEffect(() => {
    // Click outside to close
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = async (e) => {
    const query = e.target.value;
    setInputValue(query);
    setShowResults(true);

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    // Debounce search
    searchTimeout.current = setTimeout(async () => {
      setLoading(true);
      try {
        // Check if it's a ZIP code (5 digits)
        const isZip = /^\d{5}$/.test(query);

        if (isZip) {
          const location = await zipToLocation(query);
          setResults([location]);
        } else {
          const locations = await searchLocations(query);
          setResults(locations);
        }
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (location) => {
    setInputValue(location.shortName);
    setShowResults(false);
    onChange(location);
  };

  const handleClear = () => {
    setInputValue('');
    setResults([]);
    onChange(null);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => results.length > 0 && setShowResults(true)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-3 border-2 border-gray-200 rounded-lg focus:border-cyan-400 focus:outline-none transition-colors"
        />
        {inputValue && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {showResults && (results.length > 0 || loading) && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              <Loader className="w-5 h-5 animate-spin mx-auto mb-2" />
              Searching...
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No locations found
            </div>
          ) : (
            results.map((location, index) => (
              <button
                key={location.id || index}
                onClick={() => handleSelect(location)}
                className="w-full text-left px-4 py-3 hover:bg-cyan-50 transition-colors border-b border-gray-100 last:border-0"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-cyan-500 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-800">
                      {location.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {location.state && `${location.state}, `}{location.country}
                    </div>
                    {location.zipCode && (
                      <div className="text-xs text-gray-500">
                        ZIP: {location.zipCode}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {inputValue && results.length === 0 && !loading && showResults && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-xl p-4">
          <div className="text-sm text-gray-600">
            💡 Try: ZIP code (e.g., "90210") or location name (e.g., "Ocean City")
          </div>
        </div>
      )}
    </div>
  );
}
